import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { UUID } from "../sync/types";
import { Avatar, useDismiss, usePlace } from "./ui";
import { browserZone, localTime } from "../localtime";
import { t, tc } from "../i18n";

// openDM opens (or finds) the DM with someone and goes there.
export async function openDMWith(session: ReturnType<typeof useSession>, id: UUID) {
  const { channel } = await session.api.openDM([id]);
  await session.loadDMMembers();
  navigate(`/c/${channel.id}`);
}

// ProfileHost opens a person's card when anything marked data-profile="<id>"
// is clicked or activated with Enter: names, avatars, mentions, member lists.
export function ProfileHost() {
  const [open, setOpen] = useState<{ id: UUID; at: DOMRect } | null>(null);
  useEffect(() => {
    const target = (e: Event) => (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-profile]");
    const click = (e: MouseEvent) => {
      const el = target(e);
      if (!el || e.defaultPrevented) return;
      e.preventDefault();
      setOpen({ id: el.dataset.profile!, at: el.getBoundingClientRect() });
    };
    const key = (e: KeyboardEvent) => {
      const el = target(e);
      if (!el || (e.key !== "Enter" && e.key !== " ") || el.tagName === "BUTTON") return;
      e.preventDefault();
      setOpen({ id: el.dataset.profile!, at: el.getBoundingClientRect() });
    };
    addEventListener("click", click);
    addEventListener("keydown", key);
    return () => {
      removeEventListener("click", click);
      removeEventListener("keydown", key);
    };
  }, []);
  return open ? <ProfileCard key={open.id + open.at.top} id={open.id} anchor={open.at} onClose={() => setOpen(null)} /> : null;
}

export function ProfileCard({ id, anchor, onClose }: { id: UUID; anchor: DOMRect; onClose: () => void }) {
  const session = useSession();
  const state = useLantern();
  const panel = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const p = session.directory.get(id);

  useDismiss(panel, onClose);
  const style = usePlace(panel, anchor);
  useEffect(() => panel.current?.querySelector<HTMLElement>("button")?.focus(), []);

  if (!p) return null;
  const me = p.id === state.me?.id;
  const status = session.statusOf(p.id);
  const presence = p.kind === "agent" ? undefined : (state.presence.get(p.id) ?? (me ? "active" : "offline"));
  const owner = p.owner_id ? session.directory.get(p.owner_id) : undefined;
  const local = p.time_zone ? localTime(p.time_zone, state.me?.time_zone || browserZone()) : null;

  async function message() {
    setError("");
    try {
      await openDMWith(session, p!.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText("@" + p!.handle);
      setCopied(true);
    } catch {
      setError(t("Copying needs HTTPS here; the handle is @{handle}", { handle: p!.handle }));
    }
  }

  return createPortal(
    <div ref={panel} className="profile-card" style={style} role="dialog" aria-label={t("{name}'s profile", { name: p.display_name || p.handle })}>
      <div className="profile-card-top">
        <Avatar id={p.id} name={p.display_name || p.handle} size={64} presence={presence} />
        <div>
          <h3>
            {p.display_name || p.handle}
            {p.pronouns && <span className="pronouns"> ({p.pronouns})</span>}
          </h3>
          {p.title && <p className="profile-card-title">{p.title}</p>}
          <p className="muted">@{p.handle}</p>
          <p className="profile-card-tags">
            {p.kind === "agent" && <span className="agent-badge">{t("agent")}</span>}
            {p.role === "admin" && <span className="role-tag">{t("Admin")}</span>}
            {p.role === "guest" && <span className="role-tag">{t("Guest")}</span>}
            {presence && <span className={`presence-text ${presence}`}>{presence === "active" ? t("Active") : presence === "away" ? t("Away") : t("Offline")}</span>}
          </p>
        </div>
      </div>
      {status && (
        <p className="profile-card-status">
          <span>{status.emoji || "💬"}</span> {status.text}
        </p>
      )}
      <dl className="profile-card-facts">
        {owner && (
          <>
            <dt>{t("Owner")}</dt>
            <dd>
              <span className="profile-link" data-profile={owner.id} role="button" tabIndex={0}>
                {owner.display_name || owner.handle}
              </span>
            </dd>
          </>
        )}
        {p.email && (
          <>
            <dt>{t("Email")}</dt>
            <dd>
              <a href={`mailto:${p.email}`}>{p.email}</a>
            </dd>
          </>
        )}
        {local && (
          <>
            <dt>{t("Local time")}</dt>
            <dd>
              {local.time}
              {local.diff && <span className="muted"> · {local.diff}</span>}
            </dd>
          </>
        )}
        <dt>{t("Address")}</dt>
        <dd>{p.address}</dd>
      </dl>
      {error && <p className="error">{error}</p>}
      <div className="profile-card-actions">
        {!me && (
          <button className="primary" onClick={() => void message()}>
            {tc("action", "Message")}
          </button>
        )}
        {me && (
          <button
            className="primary"
            onClick={() => {
              onClose();
              navigate("/settings/profile");
            }}
          >
            {t("Edit profile")}
          </button>
        )}
        <button onClick={() => void copy()}>{copied ? t("Copied") : t("Copy handle")}</button>
      </div>
    </div>,
    document.body,
  );
}
